/**
 * External storage for File objects associated with assets
 * This is stored outside Redux to avoid non-serializable values in state
 */

// Map of assetId -> File object
const assetFiles = new Map();

/**
 * Store a File object for an asset
 * @param {string} assetId - Asset ID
 * @param {File} file - File object
 */
export function storeAssetFile(assetId, file) {
  if (assetId && file) {
    assetFiles.set(assetId, file);
  }
}

/**
 * Get a File object for an asset
 * @param {string} assetId - Asset ID
 * @returns {File|null} - File object or null if not found
 */
export function getAssetFile(assetId) {
  return assetFiles.get(assetId) || null;
}

/**
 * Remove a File object for an asset
 * @param {string} assetId - Asset ID
 */
export function removeAssetFile(assetId) {
  assetFiles.delete(assetId);
}

/**
 * Safely remove a File object for an asset, checking if upload is complete
 * @param {string} assetId - Asset ID
 * @param {Function} getUploadState - Function to get upload state from Redux (optional)
 */
export function safeRemoveAssetFile(assetId, getUploadState = null) {
  // If we have access to getUploadState, check if upload is complete
  if (getUploadState) {
    const uploadState = getUploadState(assetId);
    // Only remove file if upload is completed or failed (not uploading/queued)
    if (uploadState && (uploadState.status === 'uploading' || uploadState.status === 'queued')) {
      // Don't remove file yet - upload is still in progress
      return false;
    }
  }

  // Safe to remove the file
  assetFiles.delete(assetId);
  return true;
}

/**
 * Check if a File object exists for an asset
 * @param {string} assetId - Asset ID
 * @returns {boolean} - Whether the file exists
 */
export function hasAssetFile(assetId) {
  return assetFiles.has(assetId);
}

/**
 * Clear all stored File objects
 */
export function clearAssetFiles() {
  assetFiles.clear();
}

